import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  MicOff,
  PhoneOff,
  Send,
  Smartphone,
  UserPlus,
  X,
  FileSignature,
  ClipboardCheck,
  PartyPopper,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useUser } from '../../../../contexts/UserContext';
import { isOwnedBy } from '../ownership';
import { formatPhone } from '../leadUtils';
import {
  ensureAuthorized,
  getAccessToken,
  openAuthorizePopup,
} from '../../../../lib/ringcentral/auth';
import {
  useFetchLeadSignup,
  sendLeadSignupLink,
  claimLeadSignup,
  boardSignupApiFor,
} from '../../../../services/onboardingBoard';
import {
  FactsSection,
  AgreementSection,
  notify,
  toastApiError,
} from '../../intake/signup/signupSections';

// The signup flyover (Nic 2026-09-22): the Sales Rep signs the lead up
// WITHOUT leaving the call or the board. Full-screen, Schedule calendar
// shell; the live call collapses into the pinned bar in this header (the
// Call Command Center docks while this is open). Everything it shows lives
// on the LEAD, so closing and reopening (or a dropped call) resumes exactly
// where it was.
//
// Steps: send the link -> they register (phone or computer) -> confirm the facts
// -> send the agreement and watch them sign. The facts + agreement cards are
// the SAME components the Signups page renders.

const STEPS = [
  { key: 'link', label: 'Send the link', icon: Send },
  { key: 'register', label: 'They create their account', icon: Smartphone },
  { key: 'facts', label: 'Confirm the facts', icon: ClipboardCheck },
  { key: 'agreement', label: 'Sign the agreement', icon: FileSignature },
];

const mmss = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// Where the signup stands, derived from the server truth only.
export const deriveProgress = (data) => {
  const row = data?.row || null;
  const signed =
    data?.run?.endedReason === 'signed_up' ||
    row?.saSessionStatus === 'completed' ||
    row?.userType === 'client';
  const agreementOut =
    row?.saSessionStatus === 'sent' || row?.saSessionStatus === 'viewed';
  const done = {
    link: !!data?.invite || !!data?.registered,
    register: !!data?.registered,
    facts: !!row?.userIntakeConfirmedAt,
    agreement: signed,
  };
  const current = signed
    ? 'done'
    : !done.register
      ? done.link
        ? 'register'
        : 'link'
      : !done.facts
        ? 'facts'
        : 'agreement';
  return { row, signed, agreementOut, done, current };
};

// Step number for the tile chip ("Signup in progress, step 2 of 4").
export const signupStepNumber = (current) =>
  Math.max(1, STEPS.findIndex((s) => s.key === current) + 1);

const OnCallBar = ({ call, muted, elapsedMs, onToggleMute, onHangup }) => {
  if (!call) return null;
  if (!call.callLive) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500 dark:!bg-white/10 dark:text-slate-300">
        <span className="h-2 w-2 rounded-full bg-slate-400" />
        Call ended
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 py-1 pl-3 pr-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-[0.75]" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      On the call
      <span className="tabular-nums text-emerald-700 dark:text-emerald-300">
        {mmss(elapsedMs)}
      </span>
      <button
        type="button"
        onClick={onToggleMute}
        title={muted ? 'Unmute' : 'Mute'}
        className={cn(
          'ml-1 flex h-7 w-7 items-center justify-center rounded-full transition-colors whitespace-nowrap',
          muted
            ? 'bg-amber-500 text-white'
            : 'bg-white text-emerald-700 hover:bg-emerald-100 dark:!bg-white/10 dark:text-emerald-200',
        )}
      >
        {muted ? <MicOff size={14} /> : <Mic size={14} />}
      </button>
      <button
        type="button"
        onClick={onHangup}
        title="Hang up"
        className="flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-500 whitespace-nowrap"
      >
        <PhoneOff size={14} />
      </button>
    </span>
  );
};

const StepRail = ({ progress, viewing, setViewing }) => (
  <ol className="mb-0 list-none space-y-1 pl-0">
    {STEPS.map((s, i) => {
      const isDone = progress.done[s.key];
      const isCurrent = progress.current === s.key;
      const Icon = s.icon;
      return (
        <li key={s.key}>
          <button
            type="button"
            onClick={() => setViewing(s.key)}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left outline-none transition-[background-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 whitespace-nowrap',
              viewing === s.key
                ? 'bg-white shadow-sm ring-1 ring-slate-200 dark:!bg-white/10 dark:ring-white/10'
                : 'hover:bg-white/60 dark:hover:bg-white/5',
            )}
          >
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                isDone
                  ? 'bg-emerald-500 text-white'
                  : isCurrent
                    ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
                    : 'bg-slate-200 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
              )}
            >
              {isDone ? <Check size={15} /> : <Icon size={14} />}
            </span>
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Step {i + 1}
              </span>
              <span
                className={cn(
                  'block truncate text-sm font-semibold',
                  isCurrent || isDone
                    ? 'text-slate-800 dark:text-slate-100'
                    : 'text-slate-400',
                )}
              >
                {s.label}
              </span>
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);

// How long an armed card waits for its Send before backing out.
const ARM_MS = 8000;

const SendLinkStep = ({ data, leadId, onSent, onArmChange = null }) => {
  const [sending, setSending] = useState(null);
  // The premium "are you sure" (Nic 2026-10-08): the first click arms a
  // card in place (where it goes, Send, Cancel); the second sends. It backs
  // out by itself after ARM_MS, on Escape, or on a click anywhere else.
  const [armed, setArmed] = useState(null);
  const cardsRef = useRef(null);
  useEffect(() => {
    onArmChange?.(armed);
  }, [armed, onArmChange]);
  useEffect(() => () => onArmChange?.(null), [onArmChange]);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(null), ARM_MS);
    const onKey = (e) => {
      if (e.key === 'Escape') setArmed(null);
    };
    const onDown = (e) => {
      if (!cardsRef.current?.contains(e.target)) setArmed(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [armed]);
  const firstName = data.leadFirstName || 'them';
  const send = async (channel) => {
    setArmed(null);
    if (channel !== 'email') {
      let ok = await ensureAuthorized();
      if (!ok) ok = await openAuthorizePopup();
      if (!ok) {
        notify.error('Log into RingCentral to text the link.');
        return;
      }
    }
    setSending(channel);
    try {
      const res = await sendLeadSignupLink(leadId, {
        channel,
        ...(channel === 'email' ? {} : { rcAccessToken: getAccessToken() }),
      });
      const sent = res?.sent || {};
      notify.success(
        sent.sms && sent.email
          ? 'Link texted and emailed'
          : sent.sms
            ? 'Link texted'
            : 'Link emailed',
      );
      onSent();
    } catch (err) {
      toastApiError(err, "The link couldn't be sent.");
    } finally {
      setSending(null);
    }
  };
  const copy = async () => {
    if (!data.invite?.url) return;
    await navigator.clipboard?.writeText(data.invite.url);
    notify.success('Signup link copied');
  };
  const canText = !!data.leadPhone && !data.smsOptedOut;
  const canEmail = !!data.leadEmail;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
          Send {firstName} their signup link
        </h3>
        <p className="text-sm text-slate-500">
          They open it on their phone or computer while you stay on the line.
          Once they make their account, it shows up right here as yours.
        </p>
      </div>
      {/* Text OR email (Nic 2026-09-24: "Both" was not needed). Premium
          choice cards (Nic 2026-10-08): a colored icon tile, why you'd pick
          it, and an arrow; a click arms the card to confirm where it goes.
          Classes in pixels and border-[1px]: px-4 / border are Bootstrap
          twins. */}
      <div ref={cardsRef} className="grid gap-[12px] sm:grid-cols-2">
        {[
          {
            key: 'sms',
            label: 'Text it',
            icon: MessageSquare,
            ok: canText,
            why: 'Opens right on their phone.',
            badge: 'Fastest',
            hint: data.smsOptedOut
              ? 'Opted out of texts'
              : data.leadPhone
                ? null
                : 'No phone on file',
            to: formatPhone(data.leadPhone),
            go: 'Yes',
            tile: 'bg-sky-100 text-sky-600 dark:!bg-sky-500/15 dark:text-sky-300',
            hover:
              'hover:border-sky-300 hover:shadow-[0_8px_24px_rgba(14,165,233,0.14)] hover:ring-1 hover:ring-sky-300 dark:hover:border-sky-400/50 dark:hover:ring-sky-400/50',
            on: 'border-sky-300 bg-sky-50/60 shadow-[0_8px_24px_rgba(14,165,233,0.14)] ring-1 ring-sky-300 dark:border-sky-400/50 dark:!bg-sky-500/10 dark:ring-sky-400/50',
            btn: 'bg-sky-600 hover:bg-sky-500',
            arrow: 'text-sky-500 dark:text-sky-300',
          },
          {
            key: 'email',
            label: 'Email it',
            icon: Mail,
            ok: canEmail,
            why: "Best if they're at a computer.",
            hint: canEmail ? null : 'No email on file',
            to: data.leadEmail,
            go: 'Yes',
            tile: 'bg-violet-100 text-violet-600 dark:!bg-violet-500/15 dark:text-violet-300',
            hover:
              'hover:border-violet-300 hover:shadow-[0_8px_24px_rgba(139,92,246,0.14)] hover:ring-1 hover:ring-violet-300 dark:hover:border-violet-400/50 dark:hover:ring-violet-400/50',
            on: 'border-violet-300 bg-violet-50/60 shadow-[0_8px_24px_rgba(139,92,246,0.14)] ring-1 ring-violet-300 dark:border-violet-400/50 dark:!bg-violet-500/10 dark:ring-violet-400/50',
            btn: 'bg-violet-600 hover:bg-violet-500',
            arrow: 'text-violet-500 dark:text-violet-300',
          },
        ].map((b) => {
          const isArmed = armed === b.key;
          const tile = (
            <span
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                b.tile,
              )}
            >
              {sending === b.key ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <b.icon size={20} strokeWidth={2.25} />
              )}
            </span>
          );
          return (
            // One fixed size in both states, so arming never moves a thing.
            // No movement or timed fades on hover (Nic 2026-10-08: the lift
            // and staggered transitions made the text wobble).
            <div
              key={b.key}
              className={cn(
                'h-[76px] min-w-0 rounded-2xl border-[1px] border-slate-200 bg-white shadow-sm dark:border-white/10 dark:!bg-white/5',
                isArmed ? b.on : b.ok && !sending && b.hover,
                !b.ok && 'opacity-50',
                // The other card steps back while one is armed.
                armed && !isArmed && 'opacity-60',
              )}
            >
              {isArmed ? (
                <div className="flex h-full items-center gap-[12px] px-[14px]">
                  {tile}
                  {onArmChange ? (
                    // The Workspace lights up the phone or email row in
                    // About them, so the card stays short (long emails
                    // never fit, Nic 2026-10-08).
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-bold text-slate-800 dark:text-slate-100">
                        {b.key === 'sms' ? 'Text the link?' : 'Email the link?'}
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11.5px] text-slate-500 dark:text-slate-400">
                        Send the link to
                      </span>
                      <span
                        className="block truncate text-[14px] font-bold text-slate-800 dark:text-slate-100"
                        title={b.to}
                      >
                        {b.to}
                      </span>
                    </span>
                  )}
                  <button
                    type="button"
                    autoFocus
                    onClick={() => send(b.key)}
                    className={cn(
                      'inline-flex h-9 shrink-0 items-center min-w-[84px] justify-center whitespace-nowrap rounded-lg px-[20px] text-[13px] font-semibold leading-none text-white outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                      b.btn,
                    )}
                  >
                    {b.go}
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel"
                    onClick={() => setArmed(null)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-slate-200"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={!b.ok || !!sending}
                  onClick={() => setArmed(b.key)}
                  className="flex h-full w-full items-center gap-[14px] rounded-2xl px-[16px] text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:cursor-not-allowed"
                >
                  {tile}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-[8px]">
                      <span className="text-[15px] font-bold text-slate-800 dark:text-slate-100">
                        {b.label}
                      </span>
                      {b.badge && b.ok ? (
                        <span className="rounded-full bg-sky-100 px-[7px] py-px text-[10.5px] font-bold text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300">
                          {b.badge}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        'mt-[2px] block truncate text-[12.5px]',
                        b.hint
                          ? 'font-semibold text-amber-600 dark:text-amber-300'
                          : 'text-slate-500 dark:text-slate-400',
                      )}
                    >
                      {b.hint || b.why}
                    </span>
                  </span>
                  {b.ok ? (
                    <ChevronRight
                      size={18}
                      className={cn('shrink-0', b.arrow)}
                    />
                  ) : null}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {data.invite?.url ? (
        <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs text-slate-500 ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10">
          <span className="truncate">{data.invite.url}</span>
          <button
            type="button"
            onClick={copy}
            title="Copy the signup link"
            className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10 whitespace-nowrap"
          >
            <Copy size={13} /> Copy
          </button>
        </div>
      ) : null}
    </div>
  );
};

// Step 2 opened before any link went out (Nic 2026-10-08: it said "Waiting
// for Katrina..." when nobody had sent her anything). Quiet, no pulse, and
// one way back to step 1.
const LinkFirstStep = ({ data, onGo }) => (
  <div className="flex flex-col items-center py-10 text-center">
    <span className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:!bg-white/10 dark:text-slate-500">
      <Smartphone size={30} />
    </span>
    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
      Send the link first
    </h3>
    <p className="mt-1 max-w-md text-sm text-slate-500">
      Once {data.leadFirstName || 'they'} {data.leadFirstName ? 'has' : 'have'}{' '}
      the link, this is where you walk them through making their account.
    </p>
    <button
      type="button"
      onClick={onGo}
      className="mt-[14px] inline-flex h-9 items-center gap-[6px] whitespace-nowrap rounded-lg bg-slate-900 px-[16px] text-[13px] font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none dark:!bg-slate-100 dark:!text-slate-900 dark:hover:!bg-white"
    >
      <Send size={14} /> Go to Send the link
    </button>
  </div>
);

const WaitingStep = ({ data }) => (
  <div className="flex flex-col items-center py-10 text-center">
    <span className="relative mb-5 flex h-20 w-20 items-center justify-center">
      <span className="absolute inset-0 animate-ping rounded-full bg-sky-200/60 dark:bg-sky-400/20" />
      <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-sky-500 text-white shadow-lg">
        <Smartphone size={30} />
      </span>
    </span>
    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
      Waiting for {data.leadFirstName || 'them'} to create their account
    </h3>
    <p className="mt-1 max-w-md text-sm text-slate-500">
      Walk them through it: name, date of birth, email, phone, the text code,
      and their Social. This page moves on by itself the second they finish.
    </p>
  </div>
);

// extra: the Workspace's Hot seat transfer while the call is live (Nic
// 2026-10-23), between the news and Done.
const DoneStep = ({ data, onClose, extra = null, hideDone = false }) => (
  <div className="flex flex-col items-center py-8 text-center">
    {/* The news and the cheer side by side (Nic 2026-10-25: the big circle
        on top took too much height). */}
    <div className="flex w-full max-w-xl items-center justify-center gap-4 text-left">
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
        <PartyPopper size={24} />
      </span>
      <div className="min-w-0">
        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">
          {data.leadFirstName || 'They'} signed. They are a client.
        </h3>
        <p className="mb-0 mt-1 text-sm text-slate-500">
          Their agreement is on file and they are off your board. Onboarding
          takes it from here.
        </p>
      </div>
    </div>
    {extra}
    {hideDone ? null : (
      <button
        type="button"
        onClick={onClose}
        className="mt-6 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 dark:bg-slate-100 dark:!text-slate-900 whitespace-nowrap"
      >
        Done
      </button>
    )}
  </div>
);

// A horizontal stepper for narrower hosts (the call workspace's center).
// Labels wrap to two lines, and below 26rem of strip width they hide (Nic
// 2026-11-04: in a small Workspace "Send the link" ran into "They create
// their account"); the step's name stays on hover.
const StepStrip = ({ progress, viewing, setViewing }) => (
  <ol className="mb-0 flex list-none items-start pl-0 [container-type:inline-size]">
    {STEPS.map((s, i) => {
      const isDone = progress.done[s.key];
      const isCurrent = progress.current === s.key;
      const Icon = s.icon;
      return (
        <li key={s.key} className="flex min-w-0 flex-1 items-start">
          <button
            type="button"
            onClick={() => setViewing(s.key)}
            title={s.label}
            aria-label={s.label}
            className="group flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-0.5 text-center outline-none focus:outline-none"
          >
            <span
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-[box-shadow]',
                isDone
                  ? 'bg-emerald-500 text-white'
                  : isCurrent
                    ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
                    : 'bg-slate-200 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
                viewing === s.key ? 'ring-4 ring-sky-400/25' : '',
              )}
            >
              {isDone ? <Check size={14} /> : <Icon size={13} />}
            </span>
            <span
              className={cn(
                'line-clamp-2 break-words text-[11px] font-semibold leading-tight [@container(max-width:26rem)]:hidden',
                isCurrent || isDone
                  ? 'text-slate-700 dark:text-slate-200'
                  : 'text-slate-400',
              )}
            >
              {s.label}
            </span>
          </button>
          {i < STEPS.length - 1 ? (
            <span
              className={cn(
                'mt-[0.95rem] h-0.5 w-6 shrink-0 rounded-full sm:w-10',
                progress.done[s.key]
                  ? 'bg-emerald-400'
                  : 'bg-slate-200 dark:bg-white/10',
              )}
            />
          ) : null}
        </li>
      );
    })}
  </ol>
);

// The signup steps themselves, host-agnostic (Nic 2026-09-23): the call
// workspace renders them inline beside the person and the notes; the
// standalone flyover wraps them in its own full-screen shell. Everything
// lives on the LEAD, so any host resumes exactly where the last one left.
//   layout 'side' = step rail on the left; 'top' = horizontal stepper.
export const SignupBody = ({
  leadId,
  leadName,
  layout = 'side',
  onDone,
  // The lead's owner when the host knows it (undefined = unknown).
  ownerUserId = undefined,
  // The Workspace's rail shows the phone and email (Nic 2026-10-08): it
  // gets told which one an armed send card points at ('sms' | 'email' |
  // null) and lights that row up instead of the card spelling it out.
  onArmChange = null,
  // Shown on the "They signed" screen (the Workspace's Hot seat transfer).
  doneExtra = null,
  hideDone = false, // on a live call, Done does nothing useful
}) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data, isLoading, isError } = useFetchLeadSignup(leadId);
  const progress = useMemo(() => deriveProgress(data), [data]);
  const currentStep = progress.current;
  const [viewing, setViewing] = useState(null);
  const [factsDirty, setFactsDirty] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const api = useMemo(() => boardSignupApiFor(leadId), [leadId]);

  // Follow the work: whenever the current step advances, show it.
  useEffect(() => {
    setViewing(currentStep === 'done' ? null : currentStep);
  }, [currentStep]);

  const refresh = () => {
    queryClient.invalidateQueries(['lead-signup', leadId]);
    queryClient.invalidateQueries(['onboarding-board']);
  };

  const row = progress.row;
  // Ownership rule (Nic 2026-09-29): only the lead's owner claims its
  // signup. Unknown owner = let the server decide (it 403s a non-owner).
  const owner =
    ownerUserId !== undefined
      ? ownerUserId
      : (data?.ownerUserId ?? data?.run?.ownerUserId);
  const canClaim = owner === undefined || isOwnedBy(owner, user?.userId);
  // The card is the one owner (2026-10-06): whoever owns the card works the
  // signup; the server moves the account's claim to them on their first
  // step, so there is no "Claim it" for them.
  const claimedByMe =
    !!row &&
    (row.userType === 'client' ||
      row.pendingClientClaimedByUserId === user?.userId ||
      (owner !== undefined && !!owner && isOwnedBy(owner, user?.userId)));

  const claim = async () => {
    setClaiming(true);
    try {
      await claimLeadSignup(leadId);
      refresh();
    } catch (err) {
      toastApiError(err, "Couldn't claim this signup.");
    } finally {
      setClaiming(false);
    }
  };

  const show = viewing || progress.current;

  const content = isLoading ? (
    <div className="flex justify-center py-16 text-slate-400">
      <Loader2 className="animate-spin" />
    </div>
  ) : isError || !data ? (
    <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300">
      This signup couldn&apos;t be loaded. Close and try again.
    </div>
  ) : progress.current === 'done' && !viewing ? (
    <DoneStep
      data={data}
      onClose={onDone}
      extra={doneExtra}
      hideDone={hideDone}
    />
  ) : show === 'link' ? (
    <SendLinkStep
      data={data}
      leadId={leadId}
      onSent={refresh}
      onArmChange={onArmChange}
    />
  ) : show === 'register' ? (
    data.registered ? (
      <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300">
        Their account is created.
      </div>
    ) : !progress.done.link ? (
      <LinkFirstStep data={data} onGo={() => setViewing('link')} />
    ) : (
      <WaitingStep data={data} />
    )
  ) : !row ? (
    <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300">
      This step opens once they create their account.
    </div>
  ) : (
    <div className="space-y-3">
      {!claimedByMe ? (
        <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:!bg-amber-500/15 dark:text-amber-200">
          {canClaim ? (
            <>
              {/* Not "they signed up outside the link" (2026-10-06): a
                  reassigned card or a direct signup lands here too. */}
              This signup isn&apos;t claimed by you yet. Claim it to confirm
              their facts and send the agreement.
              <button
                type="button"
                disabled={claiming}
                onClick={claim}
                className="ml-auto rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-50 whitespace-nowrap"
              >
                Claim it
              </button>
            </>
          ) : (
            <>
              This signup isn&apos;t claimed by you, and only the lead&apos;s
              owner can claim it.
            </>
          )}
        </div>
      ) : null}
      {show === 'facts' ? (
        // VA.gov is one of the facts (Nic 2026-10-12): required, so Confirm
        // facts is the last thing pressed. It still saves on its own the
        // moment it's picked; a Yes means they land in onboarding ready to
        // work, anything else makes setting it up their first step.
        <FactsSection
          plain={layout === 'top'}
          key={`facts-${row.userId}-${row.userIntakeConfirmedAt || 'none'}`}
          api={api}
          row={row}
          claimedByMe={claimedByMe}
          onConfirmed={refresh}
          onDirtyChange={setFactsDirty}
          vaGovLead={{
            leadId,
            leadVaGovAccount: data.leadVaGovAccount,
            leadVaGovConfirmed: data.leadVaGovConfirmed,
          }}
        />
      ) : (
        <>
          <AgreementSection
            plain={layout === 'top'}
            api={api}
            row={row}
            claimedByMe={claimedByMe}
            factsDirty={factsDirty}
            onChanged={refresh}
            // Ask about VA.gov first (Nic 2026-10-10): their answer decides
            // where onboarding starts.
            sendBlocked={
              leadId && !data.leadVaGovConfirmed
                ? 'Answer the VA.gov question in Confirm the facts first.'
                : null
            }
          />
          {progress.agreementOut ? (
            <div className="flex items-center gap-2 rounded-xl bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:!bg-sky-500/15 dark:text-sky-200">
              <Loader2 size={15} className="animate-spin" />
              Waiting for them to sign. It lands here the moment they do.
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  if (layout === 'top') {
    return (
      <div className="space-y-3">
        <StepStrip progress={progress} viewing={show} setViewing={setViewing} />
        <div>{content}</div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      <aside className="border-b border-slate-200 p-4 md:w-72 md:shrink-0 md:border-b-0 md:border-r dark:border-white/10">
        <StepRail progress={progress} viewing={show} setViewing={setViewing} />
      </aside>
      <main className="min-h-0 flex-1 overflow-y-auto p-[20px] md:p-[48px]">
        <div className="mx-auto max-w-3xl">{content}</div>
      </main>
    </div>
  );
};

// The standalone flyover (no call, or a call with someone else): the same
// steps in a full-screen shell.
const SignupFlyover = ({
  leadId,
  leadName,
  ownerUserId = undefined,
  call,
  muted,
  elapsedMs,
  onToggleMute,
  onHangup,
  onClose,
}) => {
  const { data } = useFetchLeadSignup(leadId);
  const progress = useMemo(() => deriveProgress(data), [data]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name =
    `${data?.leadFirstName || ''} ${data?.leadLastName || ''}`.trim() ||
    leadName ||
    'this lead';

  return createPortal(
    <div className="fixed inset-0 z-[1070]">
      <div
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
      />
      <div className="absolute inset-2 flex flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl md:inset-6 dark:bg-surface-page">
        {/* Header: who, the live call, close */}
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-5 py-3 dark:border-white/10 dark:!bg-surface-elevated">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white">
            <UserPlus size={17} />
          </span>
          <div className="min-w-0">
            <div className="truncate text-base font-bold text-slate-800 dark:text-slate-100">
              Sign up {name}
            </div>
            <div className="text-xs text-slate-400">
              {progress.current === 'done'
                ? 'Signed'
                : `Step ${signupStepNumber(progress.current)} of ${STEPS.length}`}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <OnCallBar
              call={call}
              muted={muted}
              elapsedMs={elapsedMs}
              onToggleMute={onToggleMute}
              onHangup={onHangup}
            />
            <button
              type="button"
              onClick={onClose}
              title="Close (the signup keeps its place)"
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <SignupBody
          leadId={leadId}
          leadName={leadName}
          ownerUserId={ownerUserId}
          layout="side"
          onDone={onClose}
        />
      </div>
    </div>,
    document.body,
  );
};

export default SignupFlyover;
